import React from 'react';
import AccessibleTypeahead from 'shared/components/accessible-typeahead';
import TagGroup from 'shared/components/tag-group';
import {compact} from 'lodash';
import {FormikHandlers} from 'formik/dist/types';

export interface MultiselectProps {
  error?: string;
  id: string;
  selected: string[];
  maximumSelections?: number;
  options: (string | {_id: string; name: string})[];
  onSelect: (selected: string, typeahead: HTMLSelectElement) => void;
  onDelete: (deleted: string) => void;
  placeholder?: string;
  allowNew?: boolean;
  onBlur?: FormikHandlers['handleBlur'];
}

export default function Multiselect({
  id,
  maximumSelections,
  selected,
  options,
  onDelete,
  onSelect,
  allowNew,
  error,
  ...accessibleTypeaheadInfo
}: MultiselectProps) {
  const mappedOptions = options.map((option) => {
    if (typeof option === 'string') {
      return {_id: option, name: option};
    }

    return option;
  });

  const renderedOptions = mappedOptions.filter((option) => !selected.includes(option._id));

  const optionsById = mappedOptions.reduce((acc, option) => {
    acc[option._id] = option;

    return acc;
  }, {});

  const data = compact(selected.map((selectedId) => optionsById[selectedId]));

  const showTypeahead = !maximumSelections || selected.length < maximumSelections;

  return (
    <div>
      {showTypeahead && (
        <AccessibleTypeahead
          allowNew={allowNew}
          id={`${id}-multiselect-typeahead`}
          cy={`${id}-multiselect-typeahead`}
          {...accessibleTypeaheadInfo}
          error={error}
          onSelect={(option, typeahead): void => {
            let selectedValue;

            if (option.customOption) {
              selectedValue = option.label || option.name || 'custom';
            } else {
              selectedValue = option._id || option.name;
            }

            onSelect(selectedValue, typeahead);
          }}
          options={renderedOptions}
          minLength={0}
          labelKey="name"
          filterBy={['name']}
        />
      )}
      <TagGroup
        data={data}
        onClose={(_id: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          onDelete(_id);
        }}
      />
    </div>
  );
}
